import {
  BreakpointObserver,
  BreakpointState,
  Breakpoints,
} from '@angular/cdk/layout';
import { DatePipe, Location } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { animateTableCollapsible } from '@app/_animations/animations';
import { CaseDetailsModel } from '@app/core/models/case.model';
import { AbstractMatTableComponent } from '@app/shared/base/abstract-mat-table/abstract-mat-table.component';
import { ErrorModalComponent } from '@app/shared/components/error-modal/error-modal.component';
import { CaseActions, CaseSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { CaseDetailDialogComponent } from '../case-detail-dialog/case-detail-dialog.component';

@Component({
  selector: 'app-support-list-table',
  templateUrl: './support-list-table.component.html',
  styleUrls: ['./support-list-table.component.scss'],
  animations: [animateTableCollapsible],
})
export class SupportListTableComponent
  extends AbstractMatTableComponent
  implements OnInit, OnDestroy
{
  @Input() caseTypes = {};

  filterValues = {};
  filterSelectObj = [];
  filterDisplayedColumns = [];
  caseNumber: string | null = null;
  caseDetails: CaseDetailsModel | null = null;
  expandedElement: any | null;
  showFollowUp: boolean = false;
  dialogRef = null;
  subscriptions: Subscription = new Subscription();
  showTableFilters = false;
  cancelingCaseId: string | null = null;

  displayedColumns: string[] = [
    'statusCodeString',
    'caseNumber',
    'caseTypeCodeString',
    'customerName',
    'title',
    'createdDate',
    'modifiedDate',
    'isEscalated',
  ];
  isExtraSmall: Observable<BreakpointState> = this.breakpointObserver.observe(
    Breakpoints.XSmall
  );

  constructor(
    public datePipe: DatePipe,
    private store: Store<AppState>,
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private breakpointObserver: BreakpointObserver,
    private location: Location
  ) {
    super();

    // Object to create Filter for
    this.filterSelectObj = [
      {
        name: 'Status',
        columnProp: 'statusCodeString',
        options: [],
        type: 'select',
      },
      {
        name: 'Case No.',
        columnProp: 'caseNumber',
        options: [],
        type: 'autocomplete',
        optionsWidth: 205,
      },
      {
        name: 'Case Type',
        columnProp: 'caseTypeCodeString',
        options: [],
        type: 'select',
      },

      {
        name: 'Site',
        columnProp: 'customerName',
        options: [],
        type: 'autocomplete',
        optionsWidth: 350,
      },
    ];

    this.filterDisplayedColumns = this.filterSelectObj.map(
      fo => `filter-${fo.columnProp}`
    );
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.caseNumber = params.get('caseid');
      if (this.caseNumber) {
        this.openCaseDetailDialog(this.caseNumber);
      }
    });

    this.subscribeToCreatedCaseFollowUpSuccess();
    this.subscribeToCreatedCaseFollowUpError();
    super.ngOnInit();

    // Custom filterPredicate
    // eslint-disable-next-line func-names
    this.dataSource.filterPredicate = this.createFilter();

    this.filterSelectObj.filter(o => {
      o.options = this.getFilterObject(this.dataSource.data, o.columnProp);
    });

    this.subscriptions.add(
      this.isExtraSmall.subscribe(size => {
        if (size.matches) {
          this.showTableFilters = true;
        } else {
          this.showTableFilters = false;
        }
      })
    );

    this.subscriptions.add(
      this.store.select(CaseSelectors.cancelingCaseId).subscribe(caseId => {
        this.cancelingCaseId = caseId;
      })
    );
  }

  getAutocompleteRef(columnProp: string): string {
    return `auto${columnProp}`;
  }

  // Get Uniqu values from columns to build filter
  getFilterObject(fullObj, key) {
    const uniqChk = [];

    fullObj.filter(obj => {
      if (!uniqChk.includes(obj[key])) {
        uniqChk.push(obj[key]);
      }
      return obj;
    });
    return uniqChk;
  }

  filterChange(filter, event) {
    this.filterValues[filter.columnProp] =
      filter.type === 'select' ? event.value : event;
    this.dataSource.filter = JSON.stringify(this.filterValues);
  }

  createFilter() {
    const filterFunction = (data: any, filter: string): boolean => {
      const searchTerms = JSON.parse(filter);

      return Object.keys(searchTerms).every(key => {
        if (!searchTerms[key] || searchTerms[key].length === 0) {
          return true;
        }
        const value = data[key]?.toString().toLowerCase();

        if (Array.isArray(searchTerms[key])) {
          // For multi-select
          return searchTerms[key].some(term =>
            value.includes(term.toLowerCase())
          );
        }
        // For autocomplete
        return value.includes(searchTerms[key].toLowerCase());
      });
    };

    return filterFunction;
  }

  resetFilters() {
    this.filterValues = {};
    this.filterSelectObj.forEach(filter => (filter.modelValue = null));
    this.dataSource.filter = ''; // Reset the table.
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  openCaseDetailDialog(caseNumber: string): void {
    this.dialogRef = this.dialog.open(CaseDetailDialogComponent, {
      data: {
        caseNumber: caseNumber,
      },
      width: '60%',
      maxWidth: '100vw',
      maxHeight: '100vh',
    });

    const xsDetailsDialog = this.isExtraSmall.subscribe(size => {
      if (size.matches) {
        this.dialogRef.updateSize('calc(99vw - .3125rem)', undefined);
      }
    });

    this.dialogRef.afterClosed().subscribe(() => {
      xsDetailsDialog.unsubscribe();
      this.location.go('/support-signed-in/support-ticket-list');

      const redirectUrl = sessionStorage.getItem('postLoginRedirectUrl');
      if (redirectUrl) {
        sessionStorage.removeItem('postLoginRedirectUrl');
      }
    });

    this.dialogRef.componentInstance.expandedElementEvent.subscribe(result => {
      this.setExpandedElement(result);
    });
  }

  subscribeToCreatedCaseFollowUpSuccess() {
    const createdCaseFollowUpSubscription = this.store
      .select(CaseSelectors.getCreatedCaseFollowUpNumber)
      .subscribe(followUpCaseNumber => {
        if (followUpCaseNumber) {
          // Reset notes
          this.showFollowUp = false;
          // Do something like getting the list of comments back
          this.store.dispatch(CaseActions.resetCaseFollowUpNumber());

          this.store.dispatch(
            CaseActions.getCaseDetails({ caseNumber: followUpCaseNumber })
          );
        }
      });

    this.subscriptions.add(createdCaseFollowUpSubscription);
  }

  subscribeToCreatedCaseFollowUpError() {
    const createdCaseFollowUpErrorSubscription = this.store
      .select(CaseSelectors.getCaseCreationError)
      .subscribe(error => {
        if (error) {
          this.dialog.open(ErrorModalComponent, {
            disableClose: false,
            data: { error: error },
          });

          this.dialog.afterAllClosed.pipe(take(1)).subscribe(() => {
            this.store.dispatch(CaseActions.clearCaseCreationError());
          });
        }
      });

    this.subscriptions.add(createdCaseFollowUpErrorSubscription);
  }

  viewCaseDetails(caseElement) {
    if (this.expandedElement?.id === caseElement.id) {
      this.expandedElement = null;
      this.showFollowUp = false;
    } else {
      this.store.dispatch(
        CaseActions.getCaseDetails({ caseNumber: caseElement.caseNumber })
      );
      this.expandedElement = caseElement;
    }
  }

  setShowFollowUp(value: boolean) {
    this.showFollowUp = value;
  }

  setExpandedElement(element: any | null) {
    this.expandedElement = element;
  }
}
